import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { deriveBrandPalette, DEFAULT_BRAND_COLOR } from './brand-palette';
import type { BrandPalette } from './brand-palette';
import { applyBrandColor, previewBrandColor } from './brand-stylesheet';
import { isHexColor } from './color-space';

/**
 * Keeps the document painted in the signed-in clinic's brand colour.
 *
 * ── WHERE THE COLOUR COMES FROM ───────────────────────────────────────────
 * `useAuth().clinic.theme`, which the API returns from `/clinics/userClinics`
 * — the call the app already makes to resolve the session. There is no fetch
 * of its own here on purpose: a separate request would mean the shell renders
 * in the Dentacares palette and then repaints, a visible flash on every sign
 * in. Settings pushes a saved colour back through `updateClinic`, so a save
 * repaints everything without a round trip either.
 *
 * ── WHAT "NO COLOUR" MEANS ────────────────────────────────────────────────
 * `theme.primaryColor === null` is a clinic that has never chosen one, and it
 * renders the Dentacares palette straight out of `styles/color.css`. The
 * provider clears its stylesheet rather than writing the default values into
 * it, so there is exactly one definition of the default and it lives in the
 * CSS where the rest of the design system can see it.
 */

interface BrandThemeValue {
  /** The clinic's stored colour, or null for the Dentacares palette. */
  brandColor: string | null;
  /** What is actually on screen — the stored colour or the default. */
  resolvedColor: string;
  /** True when the clinic has chosen a colour of its own. */
  isCustom: boolean;
  /** The full derived palette for `resolvedColor`, for previews and swatches. */
  palette: BrandPalette;
  /**
   * Paints a colour without saving it, for the picker in Settings. Pass the
   * clinic's own value (or null) to end the preview.
   */
  preview: (hex: string | null) => void;
  /** Ends any preview and repaints whatever is actually stored. */
  endPreview: () => void;
}

const BrandThemeContext = createContext<BrandThemeValue | undefined>(undefined);

export function useBrandTheme(): BrandThemeValue {
  const context = useContext(BrandThemeContext);
  if (!context) {
    throw new Error('useBrandTheme must be used within a BrandThemeProvider');
  }
  return context;
}

export function BrandThemeProvider({ children }: { children: ReactNode }) {
  const { clinic, isLoading } = useAuth();

  const stored = clinic?.theme?.primaryColor;
  const brandColor = isHexColor(stored) ? stored.toLowerCase() : null;

  // A preview is deliberately NOT part of the value the rest of the app reads.
  // Screens render from CSS custom properties, which the preview has already
  // repainted; re-rendering the tree on every pointer move over a colour area
  // would cost a full render per frame for no visual gain. The one screen that
  // needs the preview's palette as DATA — Settings, for its contrast readout —
  // holds the draft hex itself and derives from that.
  const previewing = useRef(false);

  useEffect(() => {
    // A live preview outranks the stored value while the picker is open —
    // otherwise an unrelated auth refresh mid-drag snaps the page back.
    if (previewing.current) return;

    // WHILE THE SESSION IS STILL RESOLVING, DO NOTHING.
    //
    // `clinic` is null during the initial /auth/me round trip, and it is null
    // after signing out, and those two states are indistinguishable without
    // `isLoading`. Acting on the first one clears the stylesheet the boot
    // script in index.html painted from cache — which defeats the entire
    // flash-free first paint, and on a slow connection shows the Dentacares
    // pine for as long as auth takes before snapping to the clinic's
    // colour. Waiting costs nothing: the cached stylesheet is already the
    // right answer, and this effect confirms or corrects it a moment later.
    if (isLoading) return;

    // Past this point a null clinic genuinely means signed out, and clearing
    // is correct: the next person to use this device must not see the
    // previous clinic's brand on the login screen.
    applyBrandColor(brandColor);
  }, [brandColor, isLoading]);

  const preview = useCallback((hex: string | null) => {
    previewing.current = true;
    previewBrandColor(hex);
  }, []);

  const endPreview = useCallback(() => {
    previewing.current = false;
    applyBrandColor(brandColor);
  }, [brandColor]);

  const value = useMemo<BrandThemeValue>(() => {
    const resolvedColor = brandColor ?? DEFAULT_BRAND_COLOR;
    return {
      brandColor,
      resolvedColor,
      isCustom: brandColor !== null,
      palette: deriveBrandPalette(resolvedColor),
      preview,
      endPreview,
    };
  }, [brandColor, preview, endPreview]);

  return (
    <BrandThemeContext.Provider value={value}>
      {children}
    </BrandThemeContext.Provider>
  );
}
